
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import CirclesPurpleIcon from '@src/assets/icons/circles-purple.svg';
import BackgroundImage from '@src/components/BackgroundImage';
import CtaBanner from '@src/components/CtaBanner';
import InfoPageNav from '@src/components/InfoPageNav';
import DukWithTabs from '@src/components/dukWithTabs';
import Layout from '@src/components/layout/layout';
import PageSlugs from '@src/components/layout/pageSlugs';
import { filterLocaleNodesSubjects } from '@src/i18n/localeList';
import api from '@src/utils/api.js';
import { convertToPercentage, currencyToSymbol } from '@src/utils/pricing';
import { Themed } from '@theme-ui/mdx';
import { graphql } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid } from 'theme-ui';

export const query = graphql`
  query PageBySlug($slug: String!) {
    contentfulPuslapis(slug: { eq: $slug }) {
      buttonLink
      buttonText
      title
      updatedAt
      text {
        raw
      }
      layout
      features {
        title
        description {
          childMarkdownRemark {
            htmlAst
          }
        }
        icon {
          file {
            url
          }
        }
      }
      image {
        gatsbyImageData
      }
      slug
      subheading {
        childMarkdownRemark {
          htmlAst
        }
      }
      sections {
        ... on ContentfulDukKlausimuSarasas {
          id
          title
          questions {
            category
            question
            answer {
              childMarkdownRemark {
                htmlAst
              }
            }
          }
        }
        ... on ContentfulSavybiuSkiltis {
          id
          title
          layout
          features {
            title
            description {
              childMarkdownRemark {
                htmlAst
              }
            }
            icon {
              file {
                url
                details {
                  image {
                    height
                    width
                  }
                }
              }
            }
          }
          internal {
            type
          }
        }
        ... on ContentfulReklamjuoste {
          id
          internal {
            type
          }
          buttonLink
          buttonText
          title
          image {
            gatsbyImageData
          }
          description {
            childMarkdownRemark {
              htmlAst
            }
          }
        }
        ... on ContentfulAtsiliepimuSkiltis {
          id
          title
          internal {
            type
          }
        }
      }
    }
    allContentfulMokomasisDalykas(sort: { name: ASC }) {
      nodes {
        node_locale
        name
        heading
        slug
        uniqueId
        activeOn
        image {
          gatsbyImageData(
            quality: 100
            placeholder: BLURRED
            formats: [AUTO, WEBP]
          )
        }
        icon {
          gatsbyImageData(
            width: 178
            quality: 100
            placeholder: BLURRED
            formats: [AUTO, WEBP]
          )
        }
      }
    }
  }
`;

const loadData = async () => {
  const response = {
    bestTeachers: null,
    feedback: null,
  };

  const loadTeachers = api.get('teachers', {
    params: {
      limit: 6,
    },
  });

  const loadFeedback = api.get('ratings', {
    params: {
      limit: 20,
      minimumRating: 4,
    },
  });

  let teachers = [];
  let feedback = [];
  try {
    const [teachersRes, feedbackRes] = await Promise.all([
      loadTeachers,
      loadFeedback,
    ]);
    teachers = teachersRes;
    feedback = feedbackRes;
  } catch (error) {
    console.log(error);
  }

  response.bestTeachers = teachers.data.map((t) => {
    const description = t.description || '';

    return {
      id: t.userId,
      avatarUrl: t.avatarUrl,
      displayName: t.displayName,
      description:
        description.length > 220
          ? description.slice(0, 220) + '...'
          : description,
      videoUrl: t.videoUrl,
      // subject: teacher.subject,
      price: `${t.lessonPrice} ${currencyToSymbol[t.currency]}`,
      priceNumber: t.lessonPrice,
      rating: convertToPercentage(t.rating, 5),
      ratingCount: t.ratingCount,
      isFeatured: t.isFeatured,
    };
  });

  response.feedback = feedback.data.map((f) => {
    return {
      id: f.id,
      author: f.byUserDisplayName,
      text: f.comment.slice(0, 220),
      rating: convertToPercentage(f.rating, 5),
    };
  });

  return response;
};

const pageTemplate = ({ data, pageContext }) => {
  const { t, i18n } = useTranslation();
  const pageData = data.contentfulPuslapis;
  const { locale } = pageContext;
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [feedback, setFeedback] = useState([]);

  useEffect(() => {
    if (i18n.language !== pageContext.locale) {
      i18n.changeLanguage(pageContext.locale);
    }
  }, []);

  const image = getImage(pageData.image);

  const questionSections =
    pageData.sections &&
    pageData.sections.filter((section) => {
      return section.questions && section.questions.length > 0;
    });

  const bannerSections =
    pageData.sections &&
    pageData.sections.filter((section) => {
      return (
        section.internal && section.internal.type === 'ContentfulReklamjuoste'
      );
    });

  const feedbackSections =
    pageData.sections &&
    pageData.sections.filter((section) => {
      return (
        section.internal &&
        section.internal.type === 'ContentfulAtsiliepimuSkiltis'
      );
    });

  const subjectsData = filterLocaleNodesSubjects(
    data.allContentfulMokomasisDalykas.nodes,
    locale
  );

  let lastUpdated = null;
  try {
    lastUpdated = new Date(pageData.updatedAt).toLocaleDateString();
  } catch (error) {
    // failed to read last updated date
  }

  useEffect(() => {
    let script;
    if (Object.values(PageSlugs.privacyPolicy).includes(pageData.slug)) {
      script = document.createElement('script');
      script.src = `https://consent.cookiebot.com/2d4ce7bc-e5e2-498d-b21b-f29f6a7f5877/cd.js?culture=${locale}`;
      document.getElementById('CookieDeclarationContainer')?.append(script);
    }

    const load = async () => {
      const { bestTeachers, feedback } = await loadData();
      setFeedback(feedback);
    };

    if (feedbackSections && feedbackSections.length) {
      load();
    }

    return () => {
      if (script) {
        script.remove();
      }
    };
  }, []);

  let navTheme = 'light';

  if (!image) {
    navTheme = 'dark';
  }
  return (
    <Layout
      locale={locale}
      otherSlugs={pageContext.otherSlugs}
      currentSlug={pageContext.slug}
      navTheme={navTheme}
    >
      {pageData.layout === 'DUK puslapis' && (
        <>
          <BackgroundImage className="banner" image={image}>
            <Container p={4} sx={{ display: 'flex', alignItems: 'end' }}>
              <Themed.h1 sx={{ lineHeight: 1.2, marginBottom: 3 }}>
                {pageData.title}
              </Themed.h1>
            </Container>
          </BackgroundImage>

          {questionSections && (
            <DukWithTabs data={questionSections}></DukWithTabs>
          )}

          {bannerSections && bannerSections.length && (
            <CtaBanner
              variant="secondary"
              title={bannerSections[0].title}
              description={
                bannerSections[0].description.childMarkdownRemark.htmlAst
              }
              buttonText={bannerSections[0].buttonText}
              buttonLink={bannerSections[0].buttonLink}
              image={bannerSections[0].image}
              svgElements
            />
          )}
        </>
      )}

      {pageData.layout === 'Informacinis puslapis' && (
        <>
          {image && (
            <BackgroundImage className="banner vh-70" image={image}>
              <Container p={4} sx={{ display: 'flex', alignItems: 'end' }}>
                <Box>
                  {lastUpdated && (
                    <p>
                      {t('lastUpdated')} {lastUpdated}
                    </p>
                  )}
                  <Themed.h1
                    sx={{ lineHeight: 1.2, marginBottom: 3, marginTop: 0 }}
                  >
                    {pageData.title}
                  </Themed.h1>
                </Box>
              </Container>
            </BackgroundImage>
          )}

          {!image && <Box py={5} />}

          <Box className="relative">
            <CirclesPurpleIcon className="circle-bottom-right circle-purple" />

            <Container px={4}>
              <Grid columns={[1, 1, 2, '3fr 2fr']} gap={['20px', '60px']}>
                <Box
                  py={4}
                  sx={(theme) => ({
                    h1: theme.styles.h2,
                    h2: theme.styles.h3,
                    h3: theme.styles.h4,
                    h4: theme.styles.h5,
                    h5: theme.styles.h6,
                    a: theme.styles.a,
                  })}
                >
                  {pageData.text &&
                    documentToReactComponents(JSON.parse(pageData.text.raw))}

                  {Object.values(PageSlugs.privacyPolicy).includes(
                    pageData.slug
                  ) && <div id="CookieDeclarationContainer"></div>}
                </Box>
                <Box>
                  <InfoPageNav heroImage={image} locale={pageContext.locale} />
                </Box>
              </Grid>
            </Container>
          </Box>

          {bannerSections && bannerSections.length && (
            <CtaBanner
              variant="secondary"
              title={bannerSections[0].title}
              description={
                bannerSections[0].description.childMarkdownRemark.htmlAst
              }
              buttonText={bannerSections[0].buttonText}
              buttonLink={bannerSections[0].buttonLink}
              image={bannerSections[0].image}
              svgElements
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default pageTemplate;
